import React, { useState } from 'react';

/**
 * Drop-in image container that gracefully handles missing/loading images.
 *
 *   <ImageSlot src="/images/foo.jpg" alt="" ratio="16/9" />
 *
 * - If `src` is empty → shows the editorial placeholder with `label`.
 * - If `src` errors → falls back to the placeholder.
 * - Otherwise → renders the image with object-fit:cover.
 *
 * The placeholder is intentionally visible (red-dashed border + label),
 * so when the user is dropping stock images in, they can see exactly which
 * slots still need filling.
 */
const ImageSlot = ({
  src,
  alt = '',
  ratio = '16/9',
  label = 'Image slot',
  className = '',
  rounded = false,
  cover = true,
  style,
  ...rest
}) => {
  const [failed, setFailed] = useState(false);
  const showPlaceholder = !src || failed;

  const containerClass = [
    'imgslot',
    rounded ? 'imgslot--rounded' : '',
    showPlaceholder ? 'imgslot--empty' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const containerStyle = {
    aspectRatio: ratio,
    ...style,
  };

  return (
    <div className={containerClass} style={containerStyle} aria-hidden={alt ? undefined : true} {...rest}>
      {showPlaceholder ? (
        <span className="imgslot__label">{label}</span>
      ) : (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          style={{ objectFit: cover ? 'cover' : 'contain' }}
        />
      )}
    </div>
  );
};

export default ImageSlot;
